import { DateRangePickerTestHandlers } from '@/components/base/DateRangePicker/DateRangePickerTestHandlers';
import { useOnClickOutside } from '@/hooks';
import { useScreenSize } from '@/hooks/useMediaQueries';
import Button from '@/storybook/components/Button/Button';
import { Period, TimePeriod } from '@/types/date';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import styles from './DateRangePicker.module.scss';

export interface DateSelection {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

interface Props {
  handleCloseDatePicker: () => void;
  handleAcceptDateRange: (period: Period) => void;
  onDateSelectionChange?: (selection: DateSelection) => void;
  onClear?: () => void;
  value?: Period;
}

export default function DateRangePicker({
  handleCloseDatePicker,
  handleAcceptDateRange,
  onDateSelectionChange,
  onClear,
  value,
}: Props) {
  const [startDate, setStartDate] = useState(() => (value?.beginn ? value.beginn.toDate() : undefined));
  const [endDate, setEndDate] = useState(() => (value?.end ? value.end.toDate() : undefined));

  const { t } = useTranslation();
  const { height: screenHeight } = useScreenSize();

  const minDate = useMemo(() => {
    const currentDate = new Date();
    currentDate.setFullYear(currentDate.getFullYear() - 2);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }, []);

  const maxDate = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 0);
  }, []);

  const onChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start ?? undefined);
    setEndDate(end ?? undefined);
    onDateSelectionChange?.({ startDate: start ?? undefined, endDate: end ?? undefined });
  };

  const handleClear = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    onClear?.();
  };

  const accept = () => {
    const startDateToSet = startDate ? startDate : dayjs(new Date());
    handleAcceptDateRange({
      beginn: dayjs(startDateToSet),
      end: dayjs(endDate ? endDate : startDateToSet),
      period: TimePeriod.CUSTOM,
    });
    handleCloseDatePicker();
  };

  const selectedDaysCount = useMemo(
    () => (startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), 'day') + 1 : 0),
    [startDate, endDate],
  );

  const datePickerWrapperRef = useOnClickOutside(handleCloseDatePicker);
  const datePickerCTAsContainerRef = useRef<HTMLDivElement | null>(null);

  const renderDayWithTooltip = (day: number, date: Date | undefined, minSelectableDate: Date): React.ReactNode => {
    const isBeforeMinDate = date !== undefined && date < minSelectableDate;

    if (isBeforeMinDate) {
      return <div data-tooltip-id={'before-min-date-tooltip'}>{day}</div>;
    }

    return <div>{day}</div>;
  };

  useEffect(() => {
    const scrollToElement = () => {
      if (!datePickerCTAsContainerRef.current || !datePickerWrapperRef.current) return;
      const rect = datePickerCTAsContainerRef.current.getBoundingClientRect();
      const isCTAsContainerInView = rect.bottom <= window.innerHeight;
      if (screenHeight <= 1280 && !isCTAsContainerInView) {
        requestAnimationFrame(() => {
          if (datePickerWrapperRef.current) {
            datePickerWrapperRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    };
    if (datePickerWrapperRef.current) {
      requestAnimationFrame(scrollToElement);
    }
  }, [datePickerWrapperRef, screenHeight]);

  return (
    <div
      ref={datePickerWrapperRef}
      className={styles.datePickerWrapper}
      data-testid={DateRangePickerTestHandlers.wrapper}
    >
      <div className={styles.absoluteWrapper}>
        <section className={styles.datePickerFields}>
          <TooltipWrapper
            id={'before-min-date-tooltip'}
            place="top"
            className={styles.twoYearsDataLimitTooltip}
            content={t('global.filters.twoYearsDataLimit')}
          />
        </section>
        <DatePicker
          selected={startDate}
          onChange={onChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          highlightDates={[new Date()]}
          excludeDateIntervals={[{ start: new Date(), end: new Date(dayjs().add(9999, 'days').toISOString()) }]}
          maxDate={maxDate}
          minDate={minDate}
          renderDayContents={(day, date) => renderDayWithTooltip(day, date, minDate)}
        />
        <div ref={datePickerCTAsContainerRef} className={styles.actions}>
          <Button onClick={handleClear} variant="text">
            {t('global.filters.clear')}
          </Button>
          <Button onClick={accept}>{t('global.filters.ok')}</Button>
        </div>
        <span data-testid={DateRangePickerTestHandlers.daysCount} className={styles.selectedDays}>
          {t('global.filters.selectedDays', { count: selectedDaysCount })}
        </span>
      </div>
    </div>
  );
}
